import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { apiCall } from '../api/client';
import logo from '../components/BeeFluent logos/beefluent_logo_favicon_192.png';
import HiveLevel1 from '../components/hive-levels/HiveLevel1';
import HiveLevel2 from '../components/hive-levels/HiveLevel2';

// Animated scenes keyed by hive level. Levels without a scene fall back to the
// emoji display below.
const HIVE_SCENES = { 1: HiveLevel1, 2: HiveLevel2 };

function HiveDisplay({ hiveState }) {
  const { level, beeCount } = hiveState;

  const hiveEmoji = ['🪨', '🐣', '🏚️', '🏠', '🏯', '🏰'][level] || '🏰';
  const hiveLabel = ['', 'Starter Hive', 'Growing Hive', 'Busy Hive', 'Thriving Hive', 'Legendary Hive'][level] || 'Legendary Hive';

  const nextThreshold = [0, 10, 200, 500, 1000][level] || null;
  const prevThreshold = [0, 0, 10, 200, 500][level] || 0;
  const progress = nextThreshold
    ? ((beeCount - prevThreshold) / (nextThreshold - prevThreshold)) * 100
    : 100;

  // Levels with an animated scene render it as the hero, with the bee count and
  // level-up progress overlaid on the grass at the bottom of the scene.
  const Scene = HIVE_SCENES[level];
  if (Scene) {
    return (
      <div className="relative w-full max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <Scene className="block w-full rounded-2xl drop-shadow-xl" />
        <div className="absolute inset-x-4 bottom-6 sm:bottom-8 flex flex-col items-center px-4 pb-3 sm:pb-6">
          <p className="text-white font-semibold text-sm sm:text-lg drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
            🐝 {beeCount} {beeCount === 1 ? 'bee' : 'bees'} in your hive
          </p>
          {nextThreshold && (
            <div className="mt-2 w-full max-w-xs">
              <div className="flex justify-between text-xs text-white mb-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                <span>{beeCount} bees</span>
                <span>{nextThreshold} to level up</span>
              </div>
              <div className="w-full bg-white/40 rounded-full h-3 shadow-inner">
                <div
                  className="bg-amber-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center py-8 sm:py-12 px-4">
      <div className="text-8xl sm:text-9xl mb-4 drop-shadow-lg">{hiveEmoji}</div>
      <h2 className="text-xl sm:text-2xl font-bold text-amber-700">{hiveLabel}</h2>
      <p className="text-amber-600 mt-1 text-sm sm:text-base">🐝 {beeCount} {beeCount === 1 ? 'bee' : 'bees'} in your hive</p>

      {nextThreshold && (
        <div className="mt-4 w-full max-w-xs">
          <div className="flex justify-between text-xs text-amber-600 mb-1">
            <span>{beeCount} bees</span>
            <span>{nextThreshold} to level up</span>
          </div>
          <div className="w-full bg-amber-100 rounded-full h-3">
            <div
              className="bg-amber-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      )}

      {!nextThreshold && (
        <p className="text-amber-500 text-sm mt-2 italic">Maximum hive level reached!</p>
      )}
    </div>
  );
}

// Small course-icon thumbnail used in the dashboard course cards. Falls back to a
// bee emoji when the course has no uploaded icon.
function CourseIcon({ course, className = '' }) {
  return (
    <div className={`flex-shrink-0 rounded-lg bg-amber-100 border border-amber-200 overflow-hidden flex items-center justify-center text-amber-400 ${className}`}>
      {course.iconUrl ? (
        <img src={course.iconUrl} alt={`${course.title} icon`} className="w-full h-full object-cover" />
      ) : (
        <span aria-hidden="true">🐝</span>
      )}
    </div>
  );
}

function CourseCard({ course, onSelect, onLearn, onReview }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-100 p-5 hover:shadow-md transition">
      <div className="flex justify-between items-start mb-3 gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <CourseIcon course={course} className="w-12 h-12 text-xl" />
          <div className="min-w-0">
            <h3
              onClick={() => onSelect(course.id)}
              className="font-semibold text-amber-800 text-lg hover:text-amber-600 cursor-pointer"
            >
              {course.title}
            </h3>
            {course.description && (
              <p className="text-amber-600 text-sm mt-1">{course.description}</p>
            )}
          </div>
        </div>
        <span className="flex-shrink-0 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
          {course.levels.length} {course.levels.length === 1 ? 'level' : 'levels'}
        </span>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onLearn(course.id)}
          className="flex-1 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition"
        >
          Learn
        </button>
        <button
          onClick={() => onReview(course.id)}
          className="flex-1 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition"
        >
          Review
        </button>
      </div>
    </div>
  );
}

function ExploreCourseCard({ course, isOwn, onSelect }) {
  return (
    <div
      onClick={() => onSelect(course.id)}
      className="bg-white rounded-xl shadow-sm border border-amber-100 p-5 hover:shadow-md hover:border-amber-300 transition cursor-pointer flex items-start gap-3"
    >
      <CourseIcon course={course} className="w-14 h-14 text-2xl" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-amber-800 text-lg">{course.title}</h3>
          {course.isOfficial && (
            <span className="text-xs bg-amber-400 text-white px-2 py-0.5 rounded-full font-medium">🐝 Official</span>
          )}
          {isOwn && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Yours</span>
          )}
        </div>
        {course.description && (
          <p className="text-amber-600 text-sm mt-1">{course.description}</p>
        )}
        <div className="flex items-center gap-4 mt-3 text-xs text-amber-500">
          <span className="bg-amber-50 border border-amber-200 text-amber-600 px-2 py-0.5 rounded-full">
            {course.language}
          </span>
          <span>{course.levelCount} {course.levelCount === 1 ? 'level' : 'levels'}</span>
          <span>{course.entryCount} {course.entryCount === 1 ? 'card' : 'cards'}</span>
        </div>
      </div>
    </div>
  );
}

function ExploreTab({ onCourseSelect }) {
  const { token, userId } = useAuth();
  const [search, setSearch] = useState('');
  const [language, setLanguage] = useState('All');
  const [published, setPublished] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await apiCall('GET', '/courses/published', null, token);
        if (!cancelled) setPublished(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  // Language = name of the linked word database, or "Other" for standalone courses
  const courseList = (published || []).map(course => ({
    ...course,
    language: course.databaseName || 'Other'
  }));

  const languages = [...new Set(courseList.map(c => c.language))].sort((a, b) =>
    a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)
  );

  const query = search.trim().toLowerCase();
  const results = courseList
    .filter(course =>
      (language === 'All' || course.language === language) &&
      (!query ||
        course.title.toLowerCase().includes(query) ||
        (course.description || '').toLowerCase().includes(query))
    )
    .sort((a, b) =>
      (b.isOfficial === true) - (a.isOfficial === true) ||
      new Date(b.createdAt) - new Date(a.createdAt)
    );

  return (
    <>
      <div className="relative mb-4">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400">🔍</span>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search published courses..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-amber-200 rounded-xl text-amber-800 placeholder-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
        />
      </div>

      {languages.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {['All', ...languages].map(lang => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`px-3 py-1 text-sm font-medium rounded-full border transition ${
                language === lang
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-white border-amber-200 text-amber-600 hover:border-amber-400'
              }`}
            >
              {lang}
            </button>
          ))}
        </div>
      )}

      {error ? (
        <div className="text-center py-12 text-amber-500">
          <p className="text-lg">Something went wrong.</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      ) : published === null ? (
        <p className="text-center py-12 text-amber-500">Buzzing around for courses...</p>
      ) : results.length === 0 ? (
        <div className="text-center py-12 text-amber-500">
          <p className="text-lg">
            {query || language !== 'All' ? 'No courses match your filters.' : 'No published courses yet!'}
          </p>
          <p className="text-sm mt-1">
            {query || language !== 'All'
              ? 'Try a different keyword or language.'
              : 'Be the first — publish one of your courses.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {results.map(course => (
            <ExploreCourseCard
              key={course.id}
              course={course}
              isOwn={!!course.ownerId && course.ownerId === userId}
              onSelect={onCourseSelect}
            />
          ))}
        </div>
      )}
    </>
  );
}

function UserMenu({ onOpenSettings, onLogout }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Open menu"
        className="flex items-center justify-center w-9 h-9 rounded-lg text-amber-600 hover:bg-amber-50 transition"
      >
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <line x1="4" y1="7" x2="20" y2="7" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="17" x2="20" y2="17" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-white border border-amber-100 rounded-xl shadow-lg py-1 z-20">
          <button
            onClick={() => { setOpen(false); onOpenSettings(); }}
            className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50 transition"
          >
            Settings
          </button>
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50 transition"
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ isGuest }) {
  const { token, logout, login } = useAuth();
  const navigate = useNavigate();
  const onCourseSelect = (courseId) => navigate(`/courses/${courseId}`);
  const onCourseLearn = (courseId) => navigate(`/courses/${courseId}/study/learn`);
  const onCourseReview = (courseId) => navigate(`/courses/${courseId}/study/review`);
  const onCreateCourse = () => navigate('/create');
  const onOpenSettings = () => navigate('/settings');
  const { user, refreshUser } = useUser();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isGuest ? 'explore' : 'courses');

  useEffect(() => {
    // Guests have no account and own no courses; both endpoints are auth-gated.
    if (isGuest) {
      setLoading(false);
      return;
    }
    // Hive counts change as reviews are submitted, so refresh the user in the
    // background — the cached one renders immediately in the meantime.
    refreshUser().catch(err => console.error(err));
    apiCall('GET', '/courses', null, token)
      .then(setCourses)
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [token, isGuest, refreshUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <p className="text-amber-600 text-lg">Loading your hive...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-amber-100 px-4 sm:px-6 py-4 flex justify-between items-center gap-3 sticky top-0 z-10">
        <h1 className="text-xl sm:text-2xl font-bold text-amber-600 whitespace-nowrap flex items-center gap-0.5">
          <img src={logo} alt="BeeFluent logo" className="h-9 sm:h-10 w-auto" />
          BeeFluent
        </h1>
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          {isGuest ? (
            <button
              onClick={login}
              className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              <svg className="w-4 h-4" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              <span className="hidden sm:inline">Log in with Microsoft</span>
              <span className="sm:hidden">Log in</span>
            </button>
          ) : (
            <>
              <span className="text-sm text-amber-700 truncate">{user?.username}</span>
              <UserMenu onOpenSettings={onOpenSettings} onLogout={logout} />
            </>
          )}
        </div>
      </header>

      {/* Guests have no hive; show an invitation to sign in instead. */}
      {isGuest ? (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
          <div className="bg-white border border-amber-200 rounded-xl px-5 py-4 text-amber-700 text-sm">
            🐝 You're exploring as a guest. <span className="text-amber-500">Log in with Microsoft to build your hive and save your progress.</span>
          </div>
        </div>
      ) : (
        user && <HiveDisplay hiveState={user.hiveState} />
      )}

      {/* Tabs */}
      <div className={`border-b border-amber-100 bg-white ${isGuest ? 'mt-6' : ''}`}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 flex items-center gap-4 sm:gap-6">
          {!isGuest && (
            <button
              onClick={() => setActiveTab('courses')}
              className={`py-4 text-lg sm:text-xl font-bold border-b-2 transition whitespace-nowrap ${
                activeTab === 'courses'
                  ? 'border-amber-500 text-amber-800'
                  : 'border-transparent text-amber-300 hover:text-amber-500'
              }`}
            >
              Your Courses
            </button>
          )}
          <button
            onClick={() => setActiveTab('explore')}
            className={`py-4 text-lg sm:text-xl font-bold border-b-2 transition ${
              activeTab === 'explore'
                ? 'border-amber-500 text-amber-800'
                : 'border-transparent text-amber-300 hover:text-amber-500'
            }`}
          >
            Explore
          </button>
          {!isGuest && (
            <button
              onClick={onCreateCourse}
              className="ml-auto flex-shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition whitespace-nowrap"
            >
              + New<span className="hidden sm:inline"> Course</span>
            </button>
          )}
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {activeTab === 'courses' && (
          <>
            {courses.length === 0 ? (
              <div className="text-center py-12 text-amber-500">
                <p className="text-lg">No courses yet!</p>
                <p className="text-sm mt-1">Create your first course to start growing your hive.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {courses.map(course => (
                  <CourseCard key={course.id} course={course} onSelect={onCourseSelect} onLearn={onCourseLearn} onReview={onCourseReview} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'explore' && (
          <ExploreTab onCourseSelect={onCourseSelect} />
        )}
      </div>
    </div>
  );
}